import React from 'react';
// Guarded (same reasoning as DealEditPage.tsx): frontend CI clones
// shared-types@development, which does not yet carry packageTiers, so an
// unguarded named import of DEFAULT_TIER_LABELS would fail that build.
import * as SharedTypes from '@platform/shared-types';

export interface DealTierOption { id: string; label: string; sell: boolean }

/**
 * THE ONE label list — exported so `DealEditPage.tsx` builds its by-rank
 * slots from the same three words this component falls back to, rather than
 * each file carrying its own copy of `['Basic', 'Pro', 'Advanced']`.
 */
export const DEFAULT_TIER_LABELS: readonly string[] = (SharedTypes as any).DEFAULT_TIER_LABELS ?? ['Basic', 'Pro', 'Advanced'];

/**
 * Board 3 (owner 2026-10-11: "even if it's off it should show") — ALWAYS
 * renders three slots. A package not yet carved into tiers, or a tier whose
 * package row is missing or inactive, is shown greyed and unclickable rather
 * than hidden: the operator needs to see that tiers are not set up here, not
 * have the control disappear as if nothing were missing.
 */
export const DealTierPicker: React.FC<{
  name: string; currentId: string; options: DealTierOption[]; onPick: (id: string) => void;
}> = ({ name, currentId, options, onPick }) => {
  // No family at all: the three default slots, with today's single package
  // standing in for its own top (Advanced) tier — nothing here is clickable,
  // because there is no sibling tier to swap to yet.
  if (options.length === 0) {
    return (
      <div className="mt-2" title="Set up tiers on Packages">
        <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Tier</p>
        <div role="radiogroup" aria-label={`${name} tier`} className="inline-flex gap-1 rounded-lg border border-stone-200 bg-stone-100 p-1">
          {DEFAULT_TIER_LABELS.map((label, i) => {
            const selected = i === DEFAULT_TIER_LABELS.length - 1;
            return (
              <button
                key={label} type="button" role="radio" aria-checked={selected} aria-disabled disabled
                className={`min-h-[44px] px-3 rounded-md text-xs cursor-default ${selected ? 'bg-[#DC143C] text-white font-bold' : 'text-stone-400'}`}
              >
                {label}{selected ? '' : ' · Off'}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Tier</p>
      <div role="radiogroup" aria-label={`${name} tier`} className="inline-flex gap-1 rounded-lg border border-stone-200 bg-stone-100 p-1">
        {options.map((o) => {
          const on = o.id === currentId;
          const off = !o.sell;
          return (
            <button
              key={o.id} type="button" role="radio" aria-checked={on}
              aria-disabled={off || undefined} disabled={off}
              onClick={() => !on && !off && onPick(o.id)}
              className={`min-h-[44px] px-3 rounded-md text-xs ${
                on ? 'bg-[#DC143C] text-white font-bold' : off ? 'text-stone-400 cursor-default' : 'text-stone-600'
              }`}
            >
              {o.label}{off ? ' · Off' : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
};
